/**
 * useSessionAutoCapture Hook
 *
 * Automatically captures workflow changes to the session history store.
 * Uses store subscriptions with debouncing to avoid excessive snapshots.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useSessionHistoryStore } from '@/stores/sessionHistoryStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useDraftModeStore } from '@/stores/draftModeStore';
import type { WorkflowDefinition } from '@/types/workflowDefinition';
import type { ConnectionsConfiguration } from '@/types/logicApps';

const DEBOUNCE_MS = 500;

/**
 * Simple debounce function that returns a debounced version of the callback
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createDebouncedFn<T extends (...args: any[]) => void>(
  fn: T,
  delay: number
): T & { cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const debouncedFn = ((...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      fn(...args);
      timeoutId = null;
    }, delay);
  }) as T & { cancel: () => void };

  debouncedFn.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debouncedFn;
}

export function useSessionAutoCapture() {
  const isCapturing = useRef(false);
  const isCleaningUp = useRef(false);
  const prevWorkflowRef = useRef<WorkflowDefinition | null>(null);
  const prevConnectionsRef = useRef<ConnectionsConfiguration | null>(null);

  // Create debounced capture function once on mount
  const debouncedCaptureRef = useRef<(((description: string) => void) & { cancel: () => void }) | null>(null);

  const captureCurrentState = useCallback((description: string) => {
    // Prevent captures during cleanup or recursive captures
    if (isCleaningUp.current || isCapturing.current) return;

    const { history, captureSnapshot } = useSessionHistoryStore.getState();

    // Only capture if session is initialized
    if (history.length === 0) return;

    const workflow = useWorkflowStore.getState().workflow;
    const connections = useWorkflowStore.getState().connections;
    const parameters = useDraftModeStore.getState().draftParameters;
    const notes = useDraftModeStore.getState().draftNotes;

    if (!workflow) return;

    isCapturing.current = true;
    captureSnapshot({ workflow, connections, parameters, notes }, description);
    isCapturing.current = false;
  }, []);

  // Initialize debounced function once on mount
  useEffect(() => {
    debouncedCaptureRef.current = createDebouncedFn(captureCurrentState, DEBOUNCE_MS);

    return () => {
      debouncedCaptureRef.current?.cancel();
    };
  }, [captureCurrentState]);

  useEffect(() => {
    // Initialize refs with current state
    const currentState = useWorkflowStore.getState();
    prevWorkflowRef.current = currentState.workflow;
    prevConnectionsRef.current = currentState.connections;

    // Subscribe to full state changes
    const unsubscribe = useWorkflowStore.subscribe((state) => {
      // Don't capture during cleanup
      if (isCleaningUp.current) return;

      const { workflow, connections } = state;

      // Check for workflow changes
      if (workflow && prevWorkflowRef.current && workflow !== prevWorkflowRef.current) {
        debouncedCaptureRef.current?.('Workflow changed');
      }

      // Check for connection changes
      if (connections !== prevConnectionsRef.current) {
        debouncedCaptureRef.current?.('Connections changed');
      }

      // Update refs
      prevWorkflowRef.current = workflow;
      prevConnectionsRef.current = connections;
    });

    return () => {
      // Set cleanup flag first to prevent any pending captures
      isCleaningUp.current = true;
      debouncedCaptureRef.current?.cancel();
      unsubscribe();
    };
  }, []);
}
